import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash, Plus, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { registerStudent } from "../../../services/studentServices";
import { deleteImage, uploadImages } from "../../../services/uploaderServices";
import Sidebar from "../../../components/admin/common/Sidebar";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Header from "../../../components/admin/common/Header";

const AddStudent = () => {
  const navigate = useNavigate();

  // Tab state

  const [institutions, setInstitutions] = useState([
    {
      id: crypto.randomUUID(),
      name: "",
      location: "",
      degree: "",
      fieldOfStudy: "",
      yearStart: "",
      targetedYearCompletion: "",
    },
  ]);

  const [fullName, setFullName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [address, setAddress] = useState("");
  const [photo, setPhoto] = useState(null);
  const [bonafideCertificate, setBonafideCertificate] = useState(null);
  const [coursesCompleted, setCoursesCompleted] = useState("");

  const [photoUrl, setPhotoUrl] = useState(null);
  const [bonafideUrl, setBonafideUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingBonafide, setUploadingBonafide] = useState(false);

  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");

  const [loading, setLoading] = useState(false);

  const handleMobileChange = (value) => {
    const cleanedValue = value.replace(/\D/g, "");
    if (cleanedValue.length <= 10) {
      setMobile(cleanedValue);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;

    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setPhotoUrl(response.images[0].imageUrl);
        setPhoto(file);
        toast.success("Photo uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to upload photo";
      toast.error(errorMessage);
      setPhoto(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleBonafideUpload = async (file) => {
    if (!file) return;

    setUploadingBonafide(true);

    try {
      const formData = new FormData();
      formData.append("images", file);

      const response = await uploadImages(formData);

      if (response.success && response.images?.[0]?.imageUrl) {
        setBonafideUrl(response.images[0].imageUrl);
        setBonafideCertificate(file);
        toast.success("Bonafide certificate uploaded successfully");
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to upload bonafide certificate";
      toast.error(errorMessage);
      setBonafideCertificate(null);
    } finally {
      setUploadingBonafide(false);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoUrl) return;

    try {
      await deleteImage(photoUrl);
      setPhotoUrl(null);
      setPhoto(null);
      toast.success("Photo removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to delete photo";
      toast.error(errorMessage);
    }
  };

  const handleDeleteBonafide = async () => {
    if (!bonafideUrl) return;

    try {
      await deleteImage(bonafideUrl);
      setBonafideUrl(null);
      setBonafideCertificate(null);
      toast.success("Bonafide certificate removed successfully");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to delete bonafide certificate";
      toast.error(errorMessage);
    }
  };

  const handleAddInstitution = () => {
    setInstitutions((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name: "",
        location: "",
        degree: "",
        fieldOfStudy: "",
        yearStart: "",
        targetedYearCompletion: "",
      },
    ]);
  };

  const handleDeleteInstitution = (id) => {
    setInstitutions((prev) => prev.filter((inst) => inst.id !== id));
  };

  const handleInstitutionChange = (id, field, value) => {
    setInstitutions((prev) =>
      prev.map((inst) =>
        inst.id === id
          ? {
              ...inst,
              [field]: value,
            }
          : inst
      )
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      const payload = {
        fullName,
        dateOfBirth,
        email,
        mobile,
        address,
        institutionDetails: institutions.map(({ id, ...rest }) => rest),
        hasCompletedSixCourses: coursesCompleted === "Yes",
        photo: photoUrl,
        bonafideCertificate: bonafideUrl,
        isEmailVerified: true,
        isMobileVerified: true,
        status: "approved",
        isFromAdmin: true,
      };

      await registerStudent(payload);
      toast.success("Student Added successfully!");
      navigate("/admin/members/students/list");
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update student";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen p-4 gap-4 bg-white">
      <div className="flex-shrink-0">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="p-6">
                <Header title="Add Student" desc="Here u can add students" />

                <div className="container mx-auto py-6">
                  <div className="max-w-7xl mx-auto">
                    <div className="mb-6">
                      <button
                        onClick={() => navigate("/admin/members/students/list")}
                        className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors font-medium"
                      >
                        <ArrowLeft size={20} />
                        <span>Back to Students</span>
                      </button>
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                      <form onSubmit={handleSubmit}>
                        <div className="p-8 border-b border-gray-100">
                          <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            Personal Details
                          </h2>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Enter Full Name{" "}
                                <span className="text-red">*</span>
                              </label>
                              <input
                                type="text"
                                value={fullName}
                                onChange={(e) => setFullName(e.target.value)}
                                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                placeholder="Enter full name"
                              />
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Date of Birth{" "}
                                <span className="text-red">*</span>
                              </label>
                              <input
                                type="date"
                                value={dateOfBirth}
                                onChange={(e) => setDateOfBirth(e.target.value)}
                                className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Upload Photo <span className="text-red">*</span>
                              </label>
                              <input
                                type="file"
                                accept="image/*"
                                id="photoUpload"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handlePhotoUpload(file);
                                }}
                              />
                              <label
                                htmlFor="photoUpload"
                                className={`flex items-center justify-center px-4 py-2.5 border-2 border-dashed rounded-lg text-sm font-medium transition-all cursor-pointer ${
                                  uploadingPhoto
                                    ? "cursor-not-allowed bg-gray-50 border-gray-300 text-gray-400"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-purple-500 hover:text-purple-600"
                                }`}
                              >
                                {uploadingPhoto
                                  ? "Uploading..."
                                  : "Choose Photo"}
                              </label>
                              {photoUrl && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-600 truncate max-w-[200px]">
                                      {photo ? photo.name : "Current photo"}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleDeletePhoto}
                                      className="px-3 py-1 bg-red text-white text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <img
                                    src={photoUrl}
                                    alt="Uploaded photo preview"
                                    className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                  />
                                </div>
                              )}
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Upload Bonafide Image{" "}
                                <span className="text-red">*</span>
                              </label>
                              <input
                                type="file"
                                accept="image/*"
                                id="bonafideUpload"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) handleBonafideUpload(file);
                                }}
                              />
                              <label
                                htmlFor="bonafideUpload"
                                className={`flex items-center justify-center px-4 py-2.5 border-2 border-dashed rounded-lg text-sm font-medium transition-all cursor-pointer ${
                                  uploadingBonafide
                                    ? "cursor-not-allowed bg-gray-50 border-gray-300 text-gray-400"
                                    : "bg-white border-gray-300 text-gray-700 hover:border-purple-500 hover:text-purple-600"
                                }`}
                              >
                                {uploadingBonafide
                                  ? "Uploading..."
                                  : "Choose Bonafide Image"}
                              </label>
                              {bonafideUrl && (
                                <div className="mt-3 space-y-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-600 truncate max-w-[200px]">
                                      {bonafideCertificate
                                        ? bonafideCertificate.name
                                        : "Current certificate"}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={handleDeleteBonafide}
                                      className="px-3 py-1 bg-red text-white text-xs font-medium rounded-md hover:bg-red-100 transition-colors"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                  <img
                                    src={bonafideUrl}
                                    alt="Uploaded bonafide certificate preview"
                                    className="w-24 h-24 object-cover rounded-lg border border-gray-200"
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="p-8 border-b border-gray-100">
                          <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            Communication Details
                          </h2>

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Email ID <span className="text-red">*</span>
                              </label>
                              <div className="relative">
                                <input
                                  type="email"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                  placeholder="Enter email address"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">
                                Mobile (10 digits){" "}
                                <span className="text-red">*</span>
                              </label>
                              <div className="flex gap-2">
                                <div className="flex items-center justify-center px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-sm font-medium text-gray-700">
                                  +91
                                </div>
                                <input
                                  type="tel"
                                  value={mobile}
                                  onChange={(e) =>
                                    handleMobileChange(e.target.value)
                                  }
                                  className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                  placeholder="Enter 10-digit number"
                                  maxLength={10}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                              Address <span className="text-red">*</span>
                            </label>
                            <textarea
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              rows={4}
                              className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all resize-none"
                              placeholder="Enter complete address"
                            />
                          </div>
                        </div>

                        <div className="p-8 border-b border-gray-100">
                          <h2 className="text-xl font-semibold text-gray-900 mb-6">
                            Institutional Details
                          </h2>

                          {institutions.map((inst, index) => (
                            <div
                              key={inst.id}
                              className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200"
                            >
                              <div className="flex justify-between items-start mb-4">
                                <h3 className="text-sm font-semibold text-gray-700">
                                  Institution {index + 1}
                                </h3>
                                <div className="flex gap-2">
                                  {institutions.length > 1 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleDeleteInstitution(inst.id)
                                      }
                                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                      title="Delete Institution"
                                    >
                                      <Trash size={16} />
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={handleAddInstitution}
                                    className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                    title="Add Institution"
                                  >
                                    <Plus size={16} />
                                  </button>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                <div className="lg:col-span-2 space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Name of Present Institution{" "}
                                    <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.name}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "name",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter institution name"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Location <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.location}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "location",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter location"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Degree <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.degree}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "degree",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter degree"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Field of Study{" "}
                                    <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.fieldOfStudy}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "fieldOfStudy",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter field of study"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Year of Start{" "}
                                    <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.yearStart}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "yearStart",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter year"
                                  />
                                </div>

                                <div className="space-y-2">
                                  <label className="block text-sm font-medium text-gray-700">
                                    Targeted Year of Completion{" "}
                                    <span className="text-red">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={inst.targetedYearCompletion}
                                    onChange={(e) =>
                                      handleInstitutionChange(
                                        inst.id,
                                        "targetedYearCompletion",
                                        e.target.value
                                      )
                                    }
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 ring-[#4f22d8] focus:border-transparent transition-all"
                                    placeholder="Enter year"
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="p-8">
                          <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            Past, Relevant Studies
                          </h2>
                          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                            List all degree programs in aquaculture pursued
                            prior to present program providing name of the
                            institution, degree, major, number of years of study
                          </p>

                          <div className="space-y-4">
                            <p className="text-sm font-medium text-gray-700">
                              Have you completed at least six courses in
                              aquaculture or a related discipline?
                              <span className="text-red ml-1">*</span>
                            </p>

                            <div className="flex gap-6">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="coursesCompleted"
                                  value="Yes"
                                  checked={coursesCompleted === "Yes"}
                                  onChange={(e) =>
                                    setCoursesCompleted(e.target.value)
                                  }
                                  className="w-4 h-4 text-purple-600 focus:ring-2 ring-[#4f22d8]"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  Yes
                                </span>
                              </label>

                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="coursesCompleted"
                                  value="No"
                                  checked={coursesCompleted === "No"}
                                  onChange={(e) =>
                                    setCoursesCompleted(e.target.value)
                                  }
                                  className="w-4 h-4 text-purple-600 focus:ring-2 ring-[#4f22d8]"
                                />
                                <span className="text-sm font-medium text-gray-700">
                                  No
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="px-8 py-6 bg-gray-50 border-t border-gray-200 flex gap-4">
                          <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2.5 bg-[#4F22D8] text-white text-sm font-semibold rounded-lg transition-colors hover:bg-[#4119b8] disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Adding..." : "Add Student"}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              navigate("/admin/members/students/list")
                            }
                            disabled={loading}
                            className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>
    </div>
  );
};

export default AddStudent;
